/**
 * Catálogo de variáveis de contato disponíveis nos templates de disparo / envio
 * WhatsApp, e helper para montar o token Liquid correspondente.
 *
 * O token é resolvido POR CONTATO no backend quando a mensagem do disparo é
 * criada (Liquidable -> ContactDrop). Ver:
 *   - app/models/concerns/liquidable.rb (process_liquid_in_template_params)
 *   - app/drops/contact_drop.rb (name, first_name, last_name, email, phone_number
 *     e custom_attribute -> hash custom_attributes)
 *
 * Além dos atributos nativos (abaixo), os CAMPOS PERSONALIZADOS de contato da
 * conta entram como variáveis via buildCustomAttributeVariables (ClickUp
 * 86ajgczep). O backend já resolvia {{ contact.custom_attribute.<chave> }}; aqui
 * só montamos o token e a opção do seletor a partir das definições da conta.
 */

const KEY_PREFIX = 'WHATSAPP_TEMPLATES.PARSER.CONTACT_VARIABLES';

export const CONTACT_VARIABLES = [
  {
    key: 'name',
    attr: 'contact.name',
    labelKey: `${KEY_PREFIX}.NAME.LABEL`,
    previewKey: `${KEY_PREFIX}.NAME.PREVIEW`,
  },
  {
    key: 'first_name',
    attr: 'contact.first_name',
    labelKey: `${KEY_PREFIX}.FIRST_NAME.LABEL`,
    previewKey: `${KEY_PREFIX}.FIRST_NAME.PREVIEW`,
  },
  {
    key: 'last_name',
    attr: 'contact.last_name',
    labelKey: `${KEY_PREFIX}.LAST_NAME.LABEL`,
    previewKey: `${KEY_PREFIX}.LAST_NAME.PREVIEW`,
  },
  {
    key: 'email',
    attr: 'contact.email',
    labelKey: `${KEY_PREFIX}.EMAIL.LABEL`,
    previewKey: `${KEY_PREFIX}.EMAIL.PREVIEW`,
  },
  {
    key: 'phone_number',
    attr: 'contact.phone_number',
    labelKey: `${KEY_PREFIX}.PHONE.LABEL`,
    previewKey: `${KEY_PREFIX}.PHONE.PREVIEW`,
  },
];

// Índice attr -> definição, para lookup rápido (ex.: humanizar a prévia).
export const CONTACT_VARIABLE_BY_ATTR = CONTACT_VARIABLES.reduce((acc, v) => {
  acc[v.attr] = v;
  return acc;
}, {});

/**
 * Monta o token Liquid a inserir no campo da variável.
 *   sem fallback:  buildContactToken('contact.name')            -> '{{ contact.name }}'
 *   com fallback:  buildContactToken('contact.name', 'Cliente') -> "{{ contact.name | default: 'Cliente' }}"
 * Aspas simples no fallback são escapadas para não quebrar a sintaxe Liquid.
 */
export const buildContactToken = (attr, fallback = '') => {
  const trimmed = String(fallback).trim();
  if (!trimmed) return `{{ ${attr} }}`;
  const escaped = trimmed.replace(/'/g, "\\'");
  return `{{ ${attr} | default: '${escaped}' }}`;
};

// --- Campos personalizados (custom attributes) da conta ---------------------

const CUSTOM_ATTRIBUTE_PREFIX = 'contact.custom_attribute';
// Chave "limpa" (o slug gerado pela criação do campo, ex.: quem_indicou).
const SAFE_KEY = /^[a-zA-Z0-9_]+$/;

/**
 * Monta o caminho Liquid do atributo personalizado.
 *   chave slug (quem_indicou)  -> contact.custom_attribute.quem_indicou
 *   chave com hífen/ponto/etc. -> contact.custom_attribute['quem-indicou']
 * A definição permite \p{L}\p{N}_.- na chave; acesso por ponto só funciona no
 * subconjunto seguro, então caímos para colchetes (que o Liquid resolve no
 * hash) e escapamos aspas simples.
 */
export const buildCustomAttributeAttr = key => {
  const k = String(key ?? '');
  if (SAFE_KEY.test(k)) return `${CUSTOM_ATTRIBUTE_PREFIX}.${k}`;
  const escaped = k.replace(/'/g, "\\'");
  return `${CUSTOM_ATTRIBUTE_PREFIX}['${escaped}']`;
};

/**
 * Extrai a chave de um caminho de atributo personalizado (ponto ou colchete).
 * Devolve null para atributos nativos (contact.name etc.) — usado na prévia.
 */
export const parseCustomAttributeKey = attr => {
  if (typeof attr !== 'string') return null;
  const bracket = attr.match(/^contact\.custom_attribute\['(.*)'\]$/);
  if (bracket) return bracket[1].replace(/\\'/g, "'");
  const dot = attr.match(/^contact\.custom_attribute\.([a-zA-Z0-9_]+)$/);
  if (dot) return dot[1];
  return null;
};

/**
 * Converte as definições de atributo de CONTATO (do store, camelCase ou
 * snake_case) em opções do seletor de variáveis. O rótulo é o nome de exibição
 * do campo; o attr é o token Liquid pronto para buildContactToken.
 */
export const buildCustomAttributeVariables = (definitions = []) =>
  (definitions || [])
    .map(def => {
      const key = def?.attributeKey ?? def?.attribute_key;
      if (!key) return null;
      const label =
        def?.attributeDisplayName ?? def?.attribute_display_name ?? key;
      return {
        key,
        attr: buildCustomAttributeAttr(key),
        label,
        isCustom: true,
      };
    })
    .filter(Boolean);
